<script setup>
import { computed, ref, watch } from "vue";

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  application: { type: Object, default: null },
});

const emit = defineEmits(["update:modelValue", "save"]);

const DEFAULT_REQUEST_LIMIT = 100;
const DEFAULT_REQUEST_LIMIT_INTERVAL_MINUTES = 15;

const emptyForm = () => ({
  name: "",
  description: "",
  allowedIps: "",
  expiresAt: "",
  limitRequests: false,
  requestLimit: DEFAULT_REQUEST_LIMIT,
  requestLimitIntervalMinutes: DEFAULT_REQUEST_LIMIT_INTERVAL_MINUTES,
});
const form = ref(emptyForm());

const dialogVisible = computed({
  get: () => props.modelValue,
  set: (value) => emit("update:modelValue", value),
});

const isNew = computed(() => !props.application);
const canSave = computed(() => form.value.name.trim().length > 0);

const disablePastDates = (date) => date.getTime() < new Date().setHours(0, 0, 0, 0);

const resetForm = () => {
  form.value = props.application
    ? {
      name: props.application.name,
      description: props.application.description ?? "",
      allowedIps: props.application.allowedIps ?? "",
      expiresAt: props.application.expiresAt ?? "",
      limitRequests: props.application.requestLimit !== null && props.application.requestLimit !== undefined,
      requestLimit: props.application.requestLimit ?? DEFAULT_REQUEST_LIMIT,
      requestLimitIntervalMinutes: props.application.requestLimitIntervalMinutes ?? DEFAULT_REQUEST_LIMIT_INTERVAL_MINUTES,
    }
    : emptyForm();
};

const save = () => {
  if (!canSave.value) {
    return;
  }

  emit("save", {
    application: {
      name: form.value.name.trim(),
      description: form.value.description.trim() || null,
      allowedIps: form.value.allowedIps.trim() || null,
      expiresAt: form.value.expiresAt || null,
      requestLimit: form.value.limitRequests ? form.value.requestLimit : null,
      requestLimitIntervalMinutes: form.value.limitRequests ? form.value.requestLimitIntervalMinutes : null,
    },
    isNew: isNew.value,
  });
};

watch(() => [props.modelValue, props.application], resetForm, {
  immediate: true,
  deep: true,
});
</script>

<template>
  <el-dialog v-model="dialogVisible" :center="true" width="750px" :show-close="false" :lock-scroll="true"
    class="api-application-dialog">
    <section v-if="dialogVisible" class="api-application-dialog__content">
      <div class="api-application-dialog__header">
        <div class="title-text">
          <font-awesome-icon icon="key" class="api-application-dialog__icon" />
          {{ isNew ? "Nueva aplicación API" : "Editar aplicación API" }}
        </div>
        <button type="button" class="api-application-dialog__close" aria-label="Cerrar" @click="dialogVisible = false">
          <img src="@/assets/images/aspa_blue_small_entrada.png" alt="" />
        </button>
      </div>

      <div class="row api-application-dialog__body">
        <div class="col-12 el-col-xs-24">
          <div class="form-item">
            <label for="api-application-name">Nombre de la aplicación</label>
            <el-input id="api-application-name" v-model="form.name" class="w-100"
              placeholder="Ej. Integración de nómina" />
          </div>
        </div>
        <div class="col-12 el-col-xs-24">
          <div class="form-item">
            <label for="api-application-description">Descripción <span>(opcional)</span></label>
            <el-input id="api-application-description" v-model="form.description" class="w-100" type="textarea"
              :rows="4" placeholder="Describe el propósito de esta aplicación" />
          </div>
        </div>
        <div class="col-md-6 el-col-xs-24">
          <div class="form-item">
            <label for="api-application-ips">IPs permitidas <span>(opcional)</span></label>
            <el-input id="api-application-ips" v-model="form.allowedIps" class="w-100"
              placeholder="Ej. 10.0.0.5, 10.0.0.6" />
          </div>
        </div>
        <div class="col-md-6 el-col-xs-24">
          <div class="form-item date-picker">
            <label class="w-100" for="api-application-expires">Caduca <span>(opcional)</span></label>
            <el-date-picker id="api-application-expires" v-model="form.expiresAt" class="w-100" type="date"
              placeholder="Sin caducidad" format="DD/MM/YYYY" value-format="YYYY-MM-DD" clearable
              :disabled-date="disablePastDates" />
          </div>
        </div>
        <div class="col-12 el-col-xs-24">
          <div class="form-item">
            <label class="w-100" for="api-application-limit-requests">Límite de peticiones propio <span>(opcional)</span></label>
            <el-switch id="api-application-limit-requests" v-model="form.limitRequests" active-text="Sí"
              inactive-text="No" />
          </div>
        </div>
        <template v-if="form.limitRequests">
          <div class="col-md-6 el-col-xs-24">
            <div class="form-item">
              <label for="api-application-request-limit">Peticiones permitidas</label>
              <el-input-number id="api-application-request-limit" v-model="form.requestLimit" class="w-100" :min="1"
                :step="1" />
            </div>
          </div>
          <div class="col-md-6 el-col-xs-24">
            <div class="form-item">
              <label for="api-application-request-limit-interval">Cada (minutos)</label>
              <el-input-number id="api-application-request-limit-interval"
                v-model="form.requestLimitIntervalMinutes" class="w-100" :min="1" :step="1" />
            </div>
          </div>
        </template>
        <p v-else class="api-application-dialog__hint">
          Sin límite propio, se aplicará el valor por defecto ({{ DEFAULT_REQUEST_LIMIT }} peticiones / {{
            DEFAULT_REQUEST_LIMIT_INTERVAL_MINUTES }} min).
        </p>
      </div>

      <div class="api-application-dialog__actions">
        <button type="button" class="btn btn_yellow" @click="dialogVisible = false">
          CANCELAR
        </button>
        <button type="button" class="btn btn_red" :disabled="!canSave" @click="save">
          {{ isNew ? "CREAR APLICACIÓN" : "GUARDAR CAMBIOS" }}
        </button>
      </div>
    </section>
  </el-dialog>
</template>

<style lang="scss" scoped>
.api-application-dialog :deep(.el-dialog) {
  border-radius: 20px;
  max-width: calc(100vw - 24px);
}

.api-application-dialog :deep(.el-dialog__body) {
  padding: 0;
}

.api-application-dialog__content {
  padding: 4px 24px 24px;
}

.api-application-dialog__header {
  align-items: flex-start;
  display: flex;
  justify-content: space-between;
  margin-top: 2px;
}

.api-application-dialog__header .title-text {
  align-items: center;
  color: #474ce6;
  display: flex;
  font-size: 2.5em;
  font-weight: 900;
  letter-spacing: -0.05em;
  line-height: 1.15;
}

.api-application-dialog__icon {
  font-size: 36px;
  margin-right: 12px;
}

.api-application-dialog__close {
  background: transparent;
  border: 0;
  padding: 0;
}

.api-application-dialog__close img {
  display: block;
  height: 24px;
  object-fit: contain;
  width: 24px;
}

.api-application-dialog__body {
  margin: 10px 0 0;
  row-gap: 12px;
}

.api-application-dialog__body label span {
  color: #7c83a2;
  font-size: 0.85em;
  font-weight: 400;
}

.api-application-dialog__hint {
  color: #7c83a2;
  font-size: 0.9em;
  margin: 0;
}

.api-application-dialog__body :deep(.el-input-number) {
  width: 100%;
}

.api-application-dialog__body :deep(.el-input-number__decrease) {
  border-bottom-left-radius: 20px;
  border-top-left-radius: 20px;
}

.api-application-dialog__body :deep(.el-input-number__increase) {
  border-bottom-right-radius: 20px;
  border-top-right-radius: 20px;
}

.api-application-dialog__body :deep(.el-textarea__inner) {
  border-radius: 20px;
  min-height: 106px !important;
  padding: 12px 16px;
}

.api-application-dialog__actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 22px;
}

.api-application-dialog__actions .btn {
  min-height: 44px;
  width: 40%;
}

.api-application-dialog__actions .btn_red {
  color: #fff !important;
}

.api-application-dialog__actions .btn:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

@media (max-width: 767px) {
  .api-application-dialog__content {
    padding: 4px 16px 18px;
  }

  .api-application-dialog__header {
    gap: 12px;
  }

  .api-application-dialog__header .title-text {
    font-size: 26px;
  }

  .api-application-dialog__body {
    row-gap: 6px;
  }

  .api-application-dialog__actions {
    flex-direction: column;
  }

  .api-application-dialog__actions .btn {
    width: 100%;
  }
}
</style>
